import { useEffect } from 'react';
import { useRecordContext, useResourceContext } from 'react-admin';
import { useFormContext, UseFormReturn } from 'react-hook-form';
import { ChatFormTarget, useChatFormBridge } from '../context/ChatFormBridge';
import { isLockedResource, isWithheldField, publishableFields } from '../chat-form-access';

/**
 * Which inputs are actually on the screen.
 *
 * `getValues()` would give every key of the record, including ones with no input rendered - and a
 * value proposed into a field nobody can see would be saved without ever being looked at. React
 * Hook Form's mounted-field register is the honest answer to "what is on screen", so it is used
 * first; `_fields` is the fallback for a version that stops publishing it, and the record's own
 * keys are the last resort.
 *
 * Both are internal to react-hook-form. That is a deliberate trade: the alternative is a
 * hand-written field list on each of ninety screens, which is the thing that rots.
 */
function mountedFields(form: UseFormReturn): string[] {
    const control: any = form.control as any;

    const mounted: unknown = control?._names?.mount;
    if (mounted instanceof Set && mounted.size > 0) {
        return Array.from(mounted) as string[];
    }

    const registered: unknown = control?._fields;
    if (registered !== null && typeof registered === 'object') {
        const names: string[] = Object.keys(registered as object);
        if (names.length > 0) {
            return names;
        }
    }

    return Object.keys(form.getValues() ?? {});
}

/**
 * Offers this form to the assistant, and renders nothing.
 *
 * Dropped in as the last child of a `SimpleForm` or a `TabbedForm`. It has to be inside the form,
 * because that is where `useFormContext()` reaches the `FormProvider`; the registry it publishes
 * to lives above the whole layout, which is how the panel in the app bar can see a form in the
 * page.
 *
 * There is no field list to write or maintain: the fields are discovered from the form itself, so
 * a screen that gains an input gains it here too. A screen without this component is invisible to
 * the assistant, which is the default.
 *
 * **Two things it will not publish**, both from `chat-form-access.ts`: a locked resource registers
 * nothing at all, and a credential-shaped field name is withheld on every screen that does
 * register. Both are enforced here rather than left to whoever adds the component, so dropping this
 * into `OperatorEdit` by mistake does nothing rather than something quiet and bad.
 */
export const ChatFieldBridge: () => null = (): null => {
    const bridge = useChatFormBridge();
    const form: UseFormReturn = useFormContext();
    const record = useRecordContext();
    const resource: string | undefined = useResourceContext();

    const recordId: string | number | null = (record?.id as string | number) ?? null;

    useEffect((): (() => void) | void => {
        // `isLockedResource` covers the undefined case too: a form with no resource context is not
        // a screen the assistant can reason about, and publishing it would name it ''.
        if (bridge === null || form === null || isLockedResource(resource)) {
            return;
        }

        const target: ChatFormTarget = {
            resource: resource as string,
            recordId: recordId,
            // HQ-13. A function, not a snapshot, for the same reason `values` is one: a
            // `FormDataConsumer` can reveal an input long after this screen registered, and
            // the effect below does not re-run when it does.
            fields: (): string[] => publishableFields(mountedFields(form)),
            // A function rather than a snapshot: the assistant should see what the field holds
            // when a message is sent, not what it held when the screen opened.
            values: (): Record<string, unknown> => form.getValues() ?? {},
            setValue: (field: string, value: unknown): void => {
                // Withheld fields are already absent from `fields`, so a proposal for one is
                // refused before it reaches a card. Checked again here because this is the only
                // thing that can actually write into the form, and a guard on the write is worth
                // more than a guard on the offer.
                if (isWithheldField(field)) {
                    return;
                }
                // Dirty so the Save button wakes up, validated so a bad value is refused here
                // rather than by the API. Never saved: that is the user's to do.
                form.setValue(field, value, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
            },
        };

        return bridge.register(target);
    }, [bridge, form, resource, recordId]);

    return null;
};
